import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import { AuthProvider, useAuth } from './context/AuthContext.js'
import { useState, useEffect } from 'react'
import NavTabs from './components/NavTabs.jsx'
import CommandCenter from './pages/commandCenter/CommandCenter.jsx'
import DashBoard from './pages/dashBoard/Dashboard.jsx'
import Configuration from './pages/configuration/Configuration.jsx'
import Login from './pages/login/Login.jsx'
import Outage from './pages/outage/Outage.jsx';
import DeviceTrouble from './pages/deviceTrouble/DeviceTrouble.jsx';
import DeviceManagement from './pages/deviceManagement/DeviceManagement.jsx';
import { msalConfig } from './config/msalConfig.js';
import { styled } from '@mui/material/styles'
import { Box, CircularProgress, Typography, GlobalStyles } from '@mui/material'
import { FORD_COLORS, FORD_TYPOGRAPHY } from './theme/fordTheme.js';

// Styled components following Ford brand theme
const StyledAppContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: FORD_COLORS.backgroundDark,
  color: FORD_COLORS.textLight,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  fontSize: FORD_TYPOGRAPHY.body1,
  fontWeight: FORD_TYPOGRAPHY.regular,
  background: `linear-gradient(135deg, ${FORD_COLORS.primaryDark} 0%, ${FORD_COLORS.backgroundDark} 100%)`,
  lineHeight: 1.5,
}))

const StyledMainContainer = styled(Box)(({ theme }) => ({
  maxWidth: '100%',
  // margin: '4px auto',
  // padding: '0 4px 4px',
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
}))

const StyledLoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: '20px',
  backgroundColor: FORD_COLORS.backgroundDark,
  color: FORD_COLORS.textLight,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
}))

function ProtectedLayout() {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  
  // Show loading while authentication state is being determined
  if (isLoading) {
    return (
      <StyledLoadingContainer>
        <CircularProgress sx={{ color: FORD_COLORS.secondary, size: 48 }} />
        <Typography 
          variant="h6" 
          sx={{ 
            color: FORD_COLORS.textMuted,
            fontFamily: FORD_TYPOGRAPHY.fallbackFont,
            fontWeight: FORD_TYPOGRAPHY.medium,
            fontSize: FORD_TYPOGRAPHY.h6,
          }}
        >
          Loading Ford Power Monitor...
        </Typography>
      </StyledLoadingContainer>
    )
  }
  
  if (!isAuthenticated) {
    // Store the intended route in sessionStorage before redirecting to login
    if (location.pathname !== '/login') {
      console.log('💾 Storing intended route:', location.pathname)
      sessionStorage.setItem('intendedRoute', location.pathname);
    }
    return <Navigate to="/login" replace />
  }
  
  return (
    <StyledAppContainer>
      <NavTabs />
      <StyledMainContainer>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/command-center" element={<CommandCenter />} />
          <Route path="/dashboard" element={<DashBoard />} />
          <Route path="/configuration" element={<Configuration />} />
          <Route path="/outage" element={<Outage />} />
          <Route path="/device-trouble-shooting" element={<DeviceTrouble />} />
          <Route path="/device-management" element={<DeviceManagement />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </StyledMainContainer>
    </StyledAppContainer>
  )
}


// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

export default function App() {
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    // Initialize MSAL instance
    msalInstance.initialize()
      .then(() => {
        console.log('MSAL instance initialized successfully');
        setMsalInitialized(true);
      })
      .catch((error) => {
        console.error('MSAL initialization failed:', error);
        setInitError(error.message);
      });
  }, []);

  // Show loading while MSAL initializes
  if (!msalInitialized && !initError) {
    return (
      <StyledLoadingContainer>
        <CircularProgress sx={{ color: FORD_COLORS.secondary, size: 48 }} />
        <Typography 
          variant="h6" 
          sx={{ 
            color: FORD_COLORS.textMuted,
            fontFamily: FORD_TYPOGRAPHY.fallbackFont,
            fontWeight: FORD_TYPOGRAPHY.medium,
            fontSize: FORD_TYPOGRAPHY.h6,
          }}
        >
          Initializing Authentication...
        </Typography>
      </StyledLoadingContainer>
    );
  }

  // Show error if MSAL initialization failed
  if (initError) {
    return (
      <StyledLoadingContainer>
        <Typography 
          variant="h6" 
          sx={{ 
            color: FORD_COLORS.error || '#ef4444',
            fontFamily: FORD_TYPOGRAPHY.fallbackFont,
            fontWeight: FORD_TYPOGRAPHY.medium,
            fontSize: FORD_TYPOGRAPHY.h6,
            textAlign: 'center',
            maxWidth: '600px',
          }}
        >
          Authentication Initialization Failed
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: FORD_COLORS.textMuted,
            fontFamily: FORD_TYPOGRAPHY.fallbackFont,
            textAlign: 'center',
            maxWidth: '600px',
          }}
        >
          {initError}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: FORD_COLORS.textMuted,
            fontFamily: FORD_TYPOGRAPHY.fallbackFont,
            textAlign: 'center',
            maxWidth: '600px',
            marginTop: '16px',
          }}
        >
          Please refresh the page or clear your browser cache and try again.
        </Typography>
      </StyledLoadingContainer>
    );
  }
  return (
    <>
      <GlobalStyles
        styles={{
          // Import Ford Antenna font
          '@import': 'url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap")',
          
          'html, body, #root': {
            height: '100%',
            margin: 0,
            padding: 0,
            fontFamily: FORD_TYPOGRAPHY.newfont,
            fontSize: FORD_TYPOGRAPHY.body1,
            fontWeight: FORD_TYPOGRAPHY.regular,
            background: `linear-gradient(135deg, ${FORD_COLORS.primaryDark} 0%, ${FORD_COLORS.backgroundDark} 100%)`,
            color: FORD_COLORS.textLight,
            lineHeight: 1.5,
            letterSpacing: '0.01em',
          },
          '*': {
            boxSizing: 'border-box',
          },
          
          // Ford Typography Classes
          '.ford-h1': {
            fontSize: FORD_TYPOGRAPHY.h1,
            fontWeight: FORD_TYPOGRAPHY.bold,
            lineHeight: 1.2,
            color: FORD_COLORS.textLight,
            marginBottom: '1rem',
          },
          '.ford-h2': {
            fontSize: FORD_TYPOGRAPHY.h2,
            fontWeight: FORD_TYPOGRAPHY.semibold,
            lineHeight: 1.3,
            color: FORD_COLORS.textLight,
            marginBottom: '0.875rem',
          },
          '.ford-h3': {
            fontSize: FORD_TYPOGRAPHY.h3,
            fontWeight: FORD_TYPOGRAPHY.semibold,
            lineHeight: 1.3,
            color: FORD_COLORS.textLight,
            marginBottom: '0.75rem',
          },
          '.ford-body': {
            fontSize: FORD_TYPOGRAPHY.body1,
            fontWeight: FORD_TYPOGRAPHY.regular,
            lineHeight: 1.5,
            color: FORD_COLORS.textLight,
          },
          '.ford-caption': {
            fontSize: FORD_TYPOGRAPHY.caption,
            fontWeight: FORD_TYPOGRAPHY.regular,
            lineHeight: 1.4,
            color: FORD_COLORS.textMuted,
          },
          
          // Ford Button Styles
          '.ford-btn-primary': {
            backgroundColor: FORD_COLORS.primary,
            color: FORD_COLORS.textLight,
            border: 'none',
            borderRadius: '4px',
            padding: '12px 24px',
            fontSize: FORD_TYPOGRAPHY.body1,
            fontWeight: FORD_TYPOGRAPHY.medium,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: FORD_COLORS.primaryLight,
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0, 52, 120, 0.3)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
          '.ford-btn-secondary': {
            backgroundColor: FORD_COLORS.secondary,
            color: FORD_COLORS.textLight,
            border: 'none',
            borderRadius: '4px',
            padding: '12px 24px',
            fontSize: FORD_TYPOGRAPHY.body1,
            fontWeight: FORD_TYPOGRAPHY.medium,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: '#0554c7',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(6, 111, 239, 0.3)',
            },
          },
        }}
      />
      <MsalProvider instance={msalInstance}>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/*" element={<ProtectedLayout />} />
          </Routes>
        </AuthProvider>
      </MsalProvider>
    </>
  )
}

